"use client";

import * as React from "react";
import { useState, useRef, useCallback } from "react";
import ReactCrop, {
  type Crop,
  centerCrop,
  makeAspectCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, ZoomIn, ZoomOut, RotateCw } from "lucide-react";
import { cn } from "../../lib/utils";

export interface ImageCropperProps {
  isOpen: boolean;
  onClose: () => void;
  imageSrc: string;
  onCropComplete: (croppedBlob: Blob) => void;
  aspectRatio?: number;
  circularCrop?: boolean;
  outputQuality?: number;
  outputType?: "image/jpeg" | "image/png" | "image/webp";
  maxOutputSize?: number;
  title?: string;
  className?: string;
}

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
): Crop {
  return centerCrop(
    makeAspectCrop(
      { unit: "%", width: 90 },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

async function getCroppedImg(
  image: HTMLImageElement,
  crop: Crop,
  rotation: number,
  quality: number,
  outputType: string,
  maxSize?: number
): Promise<Blob> {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No 2d context");

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  const pixelCrop = {
    x: (crop.x / 100) * image.width * scaleX,
    y: (crop.y / 100) * image.height * scaleY,
    width: (crop.width / 100) * image.width * scaleX,
    height: (crop.height / 100) * image.height * scaleY,
  };

  let outputWidth = pixelCrop.width;
  let outputHeight = pixelCrop.height;

  if (maxSize && (outputWidth > maxSize || outputHeight > maxSize)) {
    const scale = maxSize / Math.max(outputWidth, outputHeight);
    outputWidth *= scale;
    outputHeight *= scale;
  }

  canvas.width = outputWidth;
  canvas.height = outputHeight;

  ctx.imageSmoothingQuality = "high";

  if (rotation !== 0) {
    ctx.save();
    ctx.translate(outputWidth / 2, outputHeight / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-outputWidth / 2, -outputHeight / 2);
  }

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputWidth,
    outputHeight
  );

  if (rotation !== 0) ctx.restore();

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Canvas is empty"));
      },
      outputType,
      quality
    );
  });
}

export function ImageCropper({
  isOpen,
  onClose,
  imageSrc,
  onCropComplete,
  aspectRatio = 1,
  circularCrop = false,
  outputQuality = 0.9,
  outputType = "image/jpeg",
  maxOutputSize = 512,
  title = "Crop Image",
  className,
}: ImageCropperProps) {
  const [crop, setCrop] = useState<Crop>();
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspectRatio));
    },
    [aspectRatio]
  );

  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + 0.1, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev - 0.1, 0.5));
  }, []);

  const handleRotate = useCallback(() => {
    setRotation((prev) => (prev + 90) % 360);
  }, []);

  const handleCrop = useCallback(async () => {
    if (!imgRef.current || !crop) return;

    setIsProcessing(true);
    try {
      const croppedBlob = await getCroppedImg(
        imgRef.current,
        crop,
        rotation,
        outputQuality,
        outputType,
        maxOutputSize
      );
      onCropComplete(croppedBlob);
      onClose();
    } catch (error) {
      console.error("Error cropping image:", error);
    } finally {
      setIsProcessing(false);
    }
  }, [crop, rotation, outputQuality, outputType, maxOutputSize, onCropComplete, onClose]);

  const handleClose = useCallback(() => {
    setCrop(undefined);
    setRotation(0);
    setZoom(1);
    onClose();
  }, [onClose]);

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 flex w-full max-w-md translate-x-[-50%] translate-y-[-50%] flex-col gap-4 bg-[#FFFAEF] p-4 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-2xl mx-4 max-h-[90vh]",
            className
          )}
        >
          <div className="flex items-center justify-between">
            <DialogPrimitive.Title className="text-base font-semibold text-[#344054]">
              {title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close
              className="rounded-full p-1.5 hover:bg-black/5 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5 text-[#667085]" />
            </DialogPrimitive.Close>
          </div>

          <div
            className={cn(
              "relative flex items-center justify-center overflow-hidden rounded-xl bg-black/5 min-h-[280px]",
              circularCrop && "[&_.ReactCrop__crop-selection]:rounded-full"
            )}
          >
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              aspect={aspectRatio}
              circularCrop={circularCrop}
              className="max-h-[50vh]"
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Crop preview"
                onLoad={onImageLoad}
                style={{
                  transform: `scale(${zoom}) rotate(${rotation}deg)`,
                  maxHeight: "50vh",
                  transition: "transform 0.2s ease",
                }}
                className="max-w-full object-contain"
              />
            </ReactCrop>
          </div>

          <div className="flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={handleZoomOut}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-black/20 bg-white hover:bg-black/5 transition-colors"
              aria-label="Zoom out"
            >
              <ZoomOut className="h-5 w-5 text-[#344054]" />
            </button>
            <div className="flex items-center gap-2 text-sm text-[#667085]">
              <span>{Math.round(zoom * 100)}%</span>
            </div>
            <button
              type="button"
              onClick={handleZoomIn}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-black/20 bg-white hover:bg-black/5 transition-colors"
              aria-label="Zoom in"
            >
              <ZoomIn className="h-5 w-5 text-[#344054]" />
            </button>
            <button
              type="button"
              onClick={handleRotate}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-black/20 bg-white hover:bg-black/5 transition-colors"
              aria-label="Rotate"
            >
              <RotateCw className="h-5 w-5 text-[#344054]" />
            </button>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 rounded-lg border border-black bg-white px-4 py-2.5 text-sm font-semibold text-[#344054] hover:bg-black/5 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCrop}
              disabled={isProcessing || !crop}
              className="flex-1 rounded-lg border border-black bg-[#101828] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1D2939] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? "Processing..." : "Apply"}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
